import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Switch, Button, Alert, CircularProgress,
  List, ListItem, ListItemText, ListItemIcon, Divider, Chip, TextField,
  InputAdornment, IconButton,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GroupsIcon from '@mui/icons-material/Groups';
import CottageIcon from '@mui/icons-material/Cottage';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import {
  getConversations, getNotificationSettings, setRoomMute, setMasterMute, changePassword,
  updateEmail, getMe,
} from '../api';
import {
  pushSupported, getNotificationPermission, enablePush, disablePush,
} from '../push';

const roomIcon = (kind) => {
  if (kind === 'house') return <CottageIcon />;
  if (kind === 'musyi' || kind === 'kwa') return <AccountTreeIcon />;
  if (kind === 'group') return <Diversity3Icon />;
  return <GroupsIcon />;
};

const NotificationSettings = () => {
  const [supported] = useState(pushSupported());
  const [permission, setPermission] = useState(getNotificationPermission());
  const [conversations, setConversations] = useState([]);
  const [mutedRooms, setMutedRooms] = useState([]);
  const [mutedUntil, setMutedUntil] = useState(null);
  const [devices, setDevices] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Change password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');

  // Email on file
  const [myEmail, setMyEmail] = useState(null); // current saved email, null while unknown
  const [emailInput, setEmailInput] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [emailError, setEmailError] = useState('');

  const load = useCallback(async () => {
    try {
      const [convs, settings, me] = await Promise.all([
        getConversations().catch(() => []),
        getNotificationSettings().catch(() => ({ mutedRooms: [], mutedUntil: null, devicesSubscribed: 0 })),
        getMe().catch(() => null),
      ]);
      setConversations(convs);
      setMutedRooms(settings.mutedRooms || []);
      setMutedUntil(settings.mutedUntil || null);
      setDevices(settings.devicesSubscribed || 0);
      setMyEmail(me?.email || '');
      setEmailInput(me?.email || '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEnable = async () => {
    setBusy(true); setError(''); setMsg('');
    try {
      const result = await enablePush();
      if (result.ok) {
        setPermission('granted');
        setMsg('Notifications are on for this device. You will be alerted about new family messages.');
        await load();
      } else if (result.reason === 'denied') {
        setError('Notifications are blocked in your browser settings. Please allow them for this site, then try again.');
      } else if (result.reason === 'unsupported') {
        setError('This browser cannot show notifications. On iPhone, add the app to your Home Screen first.');
      } else {
        setError('Could not enable notifications. Please try again.');
      }
    } catch (e) {
      setError('Could not enable notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setMsg('Notifications turned off on this device.');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const toggleRoom = async (room, currentlyMuted) => {
    setMutedRooms((prev) => (currentlyMuted ? prev.filter((r) => r !== room) : [...prev, room]));
    try {
      await setRoomMute(room, !currentlyMuted);
    } catch (e) {
      load(); // revert on failure
    }
  };

  const masterMuted = mutedUntil && new Date(mutedUntil) > new Date();

  const handlePasswordChange = async () => {
    setPwError('');
    setPwMsg('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError('Please fill in all three fields.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('Your new password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('The new password and confirmation do not match.');
      return;
    }
    setPwBusy(true);
    try {
      const data = await changePassword(currentPassword, newPassword);
      setPwMsg(data.message || 'Your password has been changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(err.response?.data?.error || 'Could not change your password. Please try again.');
    } finally {
      setPwBusy(false);
    }
  };

  const handleEmailUpdate = async () => {
    setEmailError('');
    setEmailMsg('');
    const value = emailInput.trim();
    if (!value) {
      setEmailError('Enter an email address.');
      return;
    }
    if (value === myEmail) {
      setEmailError('That is already your saved email.');
      return;
    }
    setEmailBusy(true);
    try {
      const data = await updateEmail(value);
      setMyEmail(data.email);
      setEmailInput(data.email);
      setEmailMsg('Your email has been saved.');
    } catch (err) {
      setEmailError(err.response?.data?.error || 'Could not save your email. Please try again.');
    } finally {
      setEmailBusy(false);
    }
  };

  const toggleMaster = async () => {
    try {
      const data = await setMasterMute(masterMuted ? 0 : 8); // 8-hour quiet
      setMutedUntil(data.mutedUntil);
      setMsg(masterMuted ? 'All notifications un-muted.' : 'All notifications muted for 8 hours.');
    } catch (e) {
      setError('Could not update mute setting.');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your password, and how you get alerted about new family messages.
      </Typography>

      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}
      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Change password */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LockIcon color="action" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Change your password</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            You'll need your current password to set a new one.
          </Typography>

          {pwMsg && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setPwMsg('')}>{pwMsg}</Alert>}
          {pwError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setPwError('')}>{pwError}</Alert>}

          <TextField
            label="Current password"
            type={showPasswords ? 'text' : 'password'}
            fullWidth
            margin="dense"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPasswords((v) => !v)} edge="end" aria-label="Show passwords">
                    {showPasswords ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="New password"
            type={showPasswords ? 'text' : 'password'}
            fullWidth
            margin="dense"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="At least 8 characters"
          />
          <TextField
            label="Confirm new password"
            type={showPasswords ? 'text' : 'password'}
            fullWidth
            margin="dense"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePasswordChange()}
          />

          <Button
            variant="contained"
            sx={{ mt: 1.5 }}
            disabled={pwBusy}
            onClick={handlePasswordChange}
          >
            {pwBusy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Update password'}
          </Button>
        </CardContent>
      </Card>

      {/* Email on file */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Your email</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {myEmail
              ? 'Used as a backup way to reach you and recover your account.'
              : "You don't have an email on file yet — common if the family admin set up your account with just your phone number. Adding one gives you a backup way in if you ever have trouble with SMS."}
          </Typography>

          {emailMsg && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setEmailMsg('')}>{emailMsg}</Alert>}
          {emailError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setEmailError('')}>{emailError}</Alert>}

          <TextField
            label="Email address"
            type="email"
            fullWidth
            margin="dense"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleEmailUpdate()}
          />
          <Button
            variant="contained"
            sx={{ mt: 1.5 }}
            disabled={emailBusy}
            onClick={handleEmailUpdate}
          >
            {emailBusy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (myEmail ? 'Update email' : 'Add email')}
          </Button>
        </CardContent>
      </Card>

      {/* Enable / status card */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          {!supported ? (
            <Alert severity="info">
              This browser can't show notifications. On iPhone, tap Share → "Add to Home Screen",
              open the app from there, and come back to turn them on.
            </Alert>
          ) : permission === 'granted' && devices > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationsActiveIcon color="primary" sx={{ fontSize: 36 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>Notifications are on</Typography>
                <Typography variant="body2" color="text.secondary">
                  Active on {devices} device{devices === 1 ? '' : 's'}.
                </Typography>
              </Box>
              <Button variant="outlined" color="inherit" onClick={handleDisable} disabled={busy}>
                Turn off
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationsOffIcon color="disabled" sx={{ fontSize: 36 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>Notifications are off</Typography>
                <Typography variant="body2" color="text.secondary">
                  Turn them on to know when family members message you.
                </Typography>
              </Box>
              <Button variant="contained" onClick={handleEnable} disabled={busy}>
                {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Turn on'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Master mute */}
      {permission === 'granted' && devices > 0 && (
        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>Mute everything</Typography>
              <Typography variant="body2" color="text.secondary">
                {masterMuted
                  ? `All notifications muted until ${new Date(mutedUntil).toLocaleTimeString()}.`
                  : 'Silence all notifications for 8 hours.'}
              </Typography>
            </Box>
            <Switch checked={Boolean(masterMuted)} onChange={toggleMaster} />
          </CardContent>
        </Card>
      )}

      {/* Per-conversation mute */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Mute specific rooms</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Turn off alerts for a room but keep the others. You'll still see messages when you open the app.
          </Typography>
          <List>
            {conversations.map((c, i) => {
              const muted = mutedRooms.includes(c.room);
              return (
                <React.Fragment key={c.room}>
                  {i > 0 && <Divider component="li" />}
                  <ListItem
                    secondaryAction={
                      <Switch
                        edge="end"
                        checked={!muted}
                        onChange={() => toggleRoom(c.room, muted)}
                        inputProps={{ 'aria-label': `Notifications for ${c.title}` }}
                      />
                    }
                  >
                    <ListItemIcon>{roomIcon(c.kind)}</ListItemIcon>
                    <ListItemText
                      primary={c.title}
                      secondary={muted ? 'Muted' : 'Notifications on'}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NotificationSettings;
